import {
  ClientlessAuthErrorFactory,
} from '../../ClientlessAuthErrors'
import { dcrApiclient, DynamicClientRegistrationApi } from '../../api-clients/index'
import { Environment, EnvironmentName, JWT } from '../../auth/common/configuration/AuthConfiguration'
import { DeviceInfoService } from '../../web/DeviceInfoService'
import { adobeEnvironments } from '../../ClientlessAuthTypes'
import { ClientApplication } from '../../api-clients/dcr-api/types.gen'
import { ClientRegistrationAccessTokenResponse } from '../../api-clients/dcr-api'
import { isClientApplication } from '../../util/Predicates'

const AUTH_STORE_DCR_KEY = 'com.turner.top-2.dcr'

/**
 * Manages the DCR workflow and stores the received data
 */
export class DynamicClientRegistration {
  private _clientTokenRequest?: Promise<ClientRegistrationAccessTokenResponse>

  constructor(
    private readonly _deviceInfoService: DeviceInfoService,
    private readonly _softwareStatement?: JWT,
    environment: Environment = EnvironmentName.PRODUCTION,
    private _redirectUri = '',
  ) {
    this._setEnvironment(environment)
  }

  /**
   * Run the whole DCR workflow and store the client data for next time. If this succeeds
   * then {@link accessToken} and {@link accessTokenExpiry} will be available.
   */
  public async run(): Promise<ClientRegistrationAccessTokenResponse> {
    let clientApp = await this.loadCredentials()

    if (!clientApp) {
      clientApp = await this.requestClientRegistration()
      await this.saveCredentials(clientApp)
    }

    return await this.requestClientAccessToken(clientApp)
  }

  private _setEnvironment(env: Environment): void {
    if (!adobeEnvironments[env]) {
      console.warn(`Invalid environment "${env}". Using "${EnvironmentName.PRODUCTION}" instead.`)
      env = EnvironmentName.PRODUCTION
    }

    dcrApiclient.setConfig({
      baseUrl: adobeEnvironments[env].DCR
    })
  }

  /**
   * DCR step 1 of 2
   *
   * Obtain Client Credentials. The credentials never expire. They must be cached to keep the user signed in.
   */
  private async requestClientRegistration(): Promise<ClientApplication> {
    console.debug(`[requestClientRegistration] called w/
      redirectUri: ${this._redirectUri}
      softwareStatement: ${this._softwareStatement}`)

    const { data, error, request, response } = await DynamicClientRegistrationApi.processSoftwareStatementUsingPost({
      body: {
        software_statement: this._softwareStatement as JWT,
        redirect_uri: this._redirectUri,
      },
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Info': this._deviceInfoService.getEncodedDeviceInfo(),
      },
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createDcr(error, request.url, response.status))
    }

    console.debug('[requestClientRegistration] data:', data)

    return data
  }

  /**
   * DCR step 2 of 2
   *
   * Get an access token from adobe. Requires the data they sent us in {@link requestClientRegistration}.
   * @todo: adobe says the accessToken should be cached in persistent storage until it expires:
   * https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/rest-apis/rest-api-dcr/dynamic-client-registration-faqs
   */
  private async _requestClientAccessToken(clientApp: ClientApplication): Promise<ClientRegistrationAccessTokenResponse> {
    console.debug('[_requestClientAccessToken] clientApp:', clientApp)

    const { client_id, client_secret, grant_types } = clientApp

    const { data, error, request, response } = await DynamicClientRegistrationApi.generateAccessTokenUsingPost({
      query: {
        client_id: client_id as string,
        client_secret: client_secret as string,
        grant_type: Array.isArray(grant_types) && grant_types.length > 0 ? grant_types[0] : 'client_credentials',
      }
    })

    if (error || !data) {
      if (response.status === 400) {
        // an 400 error may indicate that the client data in storage is corrupt.
        await this.deleteSavedCredentials()
      }

      return Promise.reject(ClientlessAuthErrorFactory.createDcr(error, request.url, response.status))
    }

    console.debug('[_requestClientAccessToken] data:', data)

    return data
  }

  /**
   * A sort of debounced facade, in case concurrent calls are run. This can also
   * be invoked to get a new token (if say the existing one is expiring).
   */
  private async requestClientAccessToken(clientApp: ClientApplication): Promise<ClientRegistrationAccessTokenResponse> {
    console.debug('[requestClientAccessToken] clientApp:', clientApp)

    // Wait to see if a current request will provide a token
    let token = await this._clientTokenRequest

    if (token) {
      console.debug('[requestClientAccessToken] token:', token)
      return token
    }

    this._clientTokenRequest = this._requestClientAccessToken(clientApp)
      .finally(() => {
        this._clientTokenRequest = undefined
      })

    token = await this._clientTokenRequest
    console.debug('[requestClientAccessToken] token:', token)
    return token
  }

  /**
   * Load credentials from `localStorage` if user has visited the page before
   */
  private async loadCredentials(): Promise<ClientApplication | undefined> {
    let rawData: string | null = null
    let credentials: ClientApplication | undefined

    try {
      rawData = localStorage.getItem(AUTH_STORE_DCR_KEY)
    } catch (error) {
      console.warn(`[loadCredentials] localStorage.getItem() failed: ${error}`)
    }

    if (!rawData) {
      return
    }

    try {
      credentials = JSON.parse(rawData)
    } catch (error) {
      console.warn(`[loadCredentials] JSON parsing error: ${error}`)
      await this.deleteSavedCredentials() // clear the bad data
    }

    if (isClientApplication(credentials)) {
      console.debug(`[loadCredentials] Successfully retrieved client credentials from storage. ID: ${credentials.client_id}`)
      return credentials
    } else {
      await this.deleteSavedCredentials() // clear the bad data
      return undefined
    }
  }

  /**
   * Write the new client info into `localStorage`.
   *
   * If these values disappear the user will be signed out.
   */
  private async saveCredentials(clientApp: ClientApplication): Promise<void> {
    try {
      const json = JSON.stringify(clientApp)
      localStorage.setItem(AUTH_STORE_DCR_KEY, json)
    } catch (e) {
      console.error(`[saveCredentials] localStorage.setItem() failed: ${e}`)
    }
  }

  /**
   * Clear all client credentials in case of a problem with them.
   */
  public async deleteSavedCredentials(): Promise<void> {
    try {
      localStorage.removeItem(AUTH_STORE_DCR_KEY)
    } catch (e) {
      console.error(`[deleteSavedCredentials] localStorage.removeItem() failed: ${e}`)
    }
  }
}

